<template>
    <BasePanel 
        headline="lang_CONTRACTS_LABEL_EDIT_PANEL_CAPTION_TRACKS_RELEASES"
        :alvqa="alvqa"
    >

        <template v-slot:panelContent>
            <AllTracksReleasesDialog
                :showDialog="showDialog"
                @set-all-tracks-releases="setAllTracksReleases"
                @closeDialog="closeDialog"
            />

            <v-data-table
                :headers="headers"
                :items="listItems"
                hide-default-footer
                :no-data-text="$t('lang_GENERAL_MESSAGE_NO_RECORDS_FOUND')"
                class="noMinWidth"
                :items-per-page="300"
                :page.sync="currentPage"
                :server-items-length="totalRecords"
                :options.sync="options"
                :loading="isLoading"
                :loading-text="$t('lang_GENERAL_LOADING_DATATABLE')"
                :no-results-text="$t('lang_GENERAL_LOADING_DATATABLE')"
                @update:options="updatePagingSortingOptions"
            >
            <!-- TOP SLOT -->
                <template v-slot:top>
                    <v-row class="pb-5">
                        <v-col cols="6">
                            <TracksReleasesEditPanelTopSlot />
                        </v-col>
                        <v-col cols="6">
                            <v-row class="pb-3 d-flex align-center pt-4">
                                <v-col cols="6">
                                    <BaseButton 
                                        @click="getCsvAndOpenDialog()"
                                        label="lang_LABEL_CONTRACTS_TRACKS_SET_ALL_TRACKS_RELEASES"
                                        block
                                        :alvqa="alvqa + '-button-open-dialog'"
                                    />
                                </v-col>
                                <v-col cols="4">
                                    <BaseButton 
                                        @click="addRelease"
                                        :disabled="$route.params.alvId === 'new'"
                                        label="lang_LABEL_CONTRACTS_TRACKS_ADD_NEW_RELEASE"
                                        block
                                        :alvqa="alvqa + '-button-add-row'"
                                    />
                                </v-col>
                                <v-col cols="2" class="pl-2">
                                    <BaseButton 
                                        @click="addNewRow"
                                        icon="add"
                                        label=""
                                        :disabled="$route.params.alvId === 'new'"
                                        block
                                        :alvqa="alvqa + '-button-add-row'"
                                    />
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                </template>

            <!-- Headers -->
                <template v-for="(header, i) in headers" v-slot:[`header.${header.value}`]>
                    <span :alvqa="header.alvqa" :key="i">{{ header.text }}</span>
                </template>

            <!-- Items -->
                <template v-slot:[`item.trackTitle`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip
                            :simpleText="item.title"
                            :alvqa="alvqa + '-list-row-text-track-title'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.selectedReleases`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip>
                            <template v-slot:text>{{ getSelectedReleasesTooltip(item) }}</template>
                            <template v-slot:content>
                                <span :alvqa="alvqa + '-list-row-text-release-title'">
                                    {{ getSelectedReleasesText(item) }}
                                </span>
                            </template>
                        </BaseTooltip>
                    </div>
                </template>
                <template v-slot:[`item.displayArtist`]="{ item }">
                    <div class="ellipsis">
                        <BaseTooltip
                            :simpleText="item.displayArtist"
                            :alvqa="alvqa + '-list-row-text-display-artist'"
                        />
                    </div>
                </template>
                <template v-slot:[`item.isrc`]="{ item }">
                    <div :alvqa="alvqa + '-list-row-text-isrc'">
                        {{ item.isrc }}
                    </div>
                </template>

            <!-- Buttons -->
                <template v-slot:[`item.delete`]="{ item }">
                    <BaseIcon 
                        @click="deleteRow(item)"
                        icon="delete"
                        color="iconDelete"
                        tooltipText="lang_GENERIC_CAPTION_DELETE"
                        :alvqa="alvqa + 'list-row-icon-delete'"
                    />
                </template>
                <template v-slot:[`item.inlineEdit`]="{ item }">
                    <BaseIcon   
                        :alvqa="alvqa + '-list-row-icon-inline-edit'"
                        icon="edit" 
                        tooltipText="lang_GENERAL_CAPTION_ICON_EDIT"   
                        @click.stop="editInlineFunction(item)"
                    />
                </template>
            </v-data-table>
            <v-pagination   
                v-if="numberOfPages > 1"
                total-visible="10" 
                v-model="currentPage" 
                :length="numberOfPages"
                color="pagination"
                @input="nextPage()"
            ></v-pagination>
        </template>
    </BasePanel>
</template>

<script>
import BasePanel from '@/components/_base/BasePanel'
import BaseButton from '@/components/_buttons/BaseButton'
import BaseIcon from '@/components/_buttons/BaseIcon'
import BaseTooltip from '@/components/_base/BaseTooltip'
import TracksReleasesEditPanelTopSlot from './TracksReleasesEditPanelTopSlot'
import AllTracksReleasesDialog from '@/pages/6_contracts/2_label/_edit/_panels/2_tracks/AllTracksReleasesDialog.vue'

export default {
    name: "TracksReleasesEditPanel",
    components: {
        BasePanel,
        BaseButton,
        BaseIcon,
        BaseTooltip,
        TracksReleasesEditPanelTopSlot,
        AllTracksReleasesDialog
    },
    data() {
        return {
            showDialog: false,
            alvqa: 'contracts-label-edit-form-panel-tracks-releases'
        }
    },
    computed: {
        headers() {
            return [
                {
                    text: this.$t("lang_CONTRACTS_LABEL_CAPTION_TRACKS_TITLE"), 
                    align: "left", 
                    value: "trackTitle",
                    class: 'text-no-wrap',
                    sortable: true,
                    alvqa: this.alvqa + '-header-track-title'
                },
                {
                    text: this.$t("lang_CONTRACTS_LABEL_CAPTION_RELEASE_TITLE"), 
                    align: "left", 
                    value: "selectedReleases",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-release-title'
                },
                {
                    text: this.$t("lang_LABEL_TRACKS_CAPTION_DISPLAY_ARTIST"), 
                    align: "left", 
                    value: "displayArtist",
                    class: 'text-no-wrap',
                    sortable: false,
                    alvqa: this.alvqa + '-header-display-artist'
                },
                {
                    text: this.$t("lang_GENERAL_CAPTION_ISRC"), 
                    align: "left", 
                    value: "isrc",
                    class: 'text-no-wrap',
                    width: '150px',
                    sortable: true,
                    alvqa: this.alvqa + '-header-isrc'
                },
                {
                    text: '', 
                    value: 'inlineEdit',
                    align: 'right',
                    sortable: false,
                    width: "70px",
                    class: 'text-no-wrap'
                },
                {
                    text: "",
                    align: "right",
                    width: "70px",
                    value: "delete",
                    sortable: false,
                    class: 'text-no-wrap'
                }
            ]
        },

        listItems() { 
            let tracksReleases = this.$store.getters.getLabelContractsTracksReleases
            return tracksReleases.filter(element => { return element !== undefined })
        },

        // Paging
        currentPage: {
            get() { return this.$store.getters.getLabelContractsTracksReleasesCurrentPage },
            set(page) { this.$store.commit('setLabelContractsTracksReleasesCurrentPage', page)}
        },
        numberOfPages() { return this.$store.getters.getLabelContractsTracksReleasesNumberOfPages },
        itemsPerPage() { return this.$store.getters.getItemsPerPage },
        totalRecords() { return this.$store.getters.getLabelContractsTracksReleasesTotalRecords },
        isLoading() { return this.$store.getters.getIsLoading },

        options: { 
            get() { return this.$store.getters.getLabelContractsSortingPagingOptions },
            set(options) { this.$store.commit('setLabelContractsSortingPagingOptions', options) }
        }
    },
    
    methods: {
        getCsvAndOpenDialog() {
            this.$store.dispatch('fetchDialogCsvLink')
            this.showDialog = true
        },
        closeDialog() {
            this.showDialog = false
            this.$store.commit('setDialogSvgLink', null)
        },
        setAllTracksReleases() {
            this.$store.dispatch('setAllTracksReleases').then(() => {
                this.$store.dispatch('fetchLabelContractsTracksReleases')
                this.closeDialog()
            })
        },
        deleteRow(item) {
            // set the selected row that should be deleted from the delete action 
            // and open the deletion dialog
            this.$store.commit("setSelectedLabelContractsTracksReleases", item.alvId)
            this.$store.commit('setDeletionDialogContext', 'SelectedLabelContractsTracksReleases')
            this.$store.commit('setShowDeletionDialog', true)
        },
        editInlineFunction(item) {
            this.$store.commit('setLabelContractsIsTrackTitelReadOnly', true)
            this.$store.commit("setSelectedLabelContractsTracksReleases", item.id)
            let detailsDeepCopy = JSON.parse(JSON.stringify(item))

            this.$store.commit('setLabelContractsTracksReleasesEditingDialogDetails', detailsDeepCopy)
            this.$store.commit('setEditingDialogHeadline', this.$t("lang_GENERAL_CAPTION_EDITING_ENTRY")) 
            this.$store.commit('setShowLabelContractsTracksReleasesEditingDialog', true)
        },
        addNewRow(){
            this.$store.commit('setLabelContractsIsTrackTitelReadOnly', false)
            this.$store.commit('setShowLabelContractsTracksReleasesEditingDialog', true)
            this.$store.commit('setEditingDialogHeadline', this.$t('lang_GENERAL_CAPTION_NEW_ENTRY'))

            let defaultValues = JSON.parse(JSON.stringify(this.$store.getters.getDefaultLabelContractsTracksReleases)) 
            this.$store.commit('setLabelContractsTracksReleasesEditingDialogDetails', defaultValues)
        },
        addRelease() {
            let alvId= this.$store.getters.getLabelContractDetailsAlvId
            
            if(alvId !== 'ALV000000000CL') this.$store.commit('setShowSearchDialog', true)
            else {
                let contractName = this.$store.getters.getLabelContractDetailsContractName
                let licensor = this.$store.getters.getLabelContractDetailsLicensor
                let contractDate = this.$store.getters.getLabelContractDetailsContractDate

                let isMainFormValidated = contractName !== null && licensor !== null && contractDate !== null

                if(isMainFormValidated) {
                    this.$store.dispatch('saveLabelContractForm')
                    this.$store.commit('setShowSearchDialog', true)
                } else {
                    this.$store.commit('setSnackbar', { 
                        type: 'error', 
                        msg: "Bitte erst die Pflichtfelder des Hauptformulars ausfüllen!"
                    })
                }
            }
        },

        getSelectedReleasesText(track) {
            if(track.selectedReleases.length == 0) return ''

            if(track.selectedReleases.length > 1) return this.$t('lang_GENERAL_CAPTION_MULTIPLE')
            else {
                // is not working with add new row, because my autocomplete is not setting the correct attribute... 
                // track is the whole thing and inside of this we have "releases"...
                let match = track.releases.find(release => {
                    return release.alvId === track.selectedReleases[0]
                })

                if(match !== undefined) {
                    let temp = match.title
                    temp += ' (EAN: ' + (match.ean !== null ?  match.ean : this.$t('lang_GENERIC_CAPTION_NOMEN_NOMINANDUM'))
                    temp += ', ' + this.$t('lang_GENERIC_CAPTION_CATALOGUE_NUMBER')+ ': ' + (match.catalogueNumber !== null ? match.catalogueNumber : this.$t('lang_GENERIC_CAPTION_NOMEN_NOMINANDUM')) + ')'
                    return temp
                } else return ''
            } 
        },
        getSelectedReleasesTooltip(track) {
            if(track.selectedReleases.length == 0) return ''

            let releases = []
            track.selectedReleases.forEach(selectedRelease => {
                let match = track.releases.find(release => {
                    return release.alvId === selectedRelease
                })
                if(match !== undefined) {
                    let temp = match.title
                    temp += ' (EAN: ' + (match.ean !== null ?  match.ean : '-')
                    temp += ', ' + this.$t('lang_GENERIC_CAPTION_CATALOGUE_NUMBER')+ ': ' + (match.catalogueNumber !== null ? match.catalogueNumber : '-') + ')'
                    releases.push(temp)
                }
            })

            return releases.join(', ')
        },
        // // Paging
		// pages() {
        //     if (this.itemsPerPage == null || this.listItems.length == null) return 0 
        //     return Math.ceil(this.listItems / this.itemsPerPage)
        // },
        
        async nextPage() {
            await this.$store.dispatch('fetchLabelContractsTracksReleases')
            // this.$vuetify.goTo('#listPage')
        },
        updatePagingSortingOptions() {
            this.$store.dispatch('fetchLabelContractsTracksReleases')
        }
    }
}
</script>

<style>
    .noMinWidth table {
        min-width: 0px !important;
        table-layout: fixed;
    }
</style>